import React from 'react';
import { useScheduleContext } from '../../../../context/ScheduleContext';
import { getGridTimelineData } from '../../../../helpers/schedule-helper';
import { unixDiff, unixFormat } from '../../../../helpers/times';
import cloneDeep from 'lodash.clonedeep';
import { GRID_VIEW_MINUTE_STEP } from '../../../../constants/page-settings/schedule';
import ScheduleGridStreams from '../../../Schedule/ScheduleComponents/ScheduleGrid/ScheduleGridStreams';
import PdfCardGrid from '../components/GridCards';

import './style.scss';

const SchedulePdfGrid = ({schedule, pdfSettings}) => {
    const {
        event,
        page,
    } = useScheduleContext();
    const [timelineStep, setTimelineStep] = React.useState(GRID_VIEW_MINUTE_STEP);

    const timelineData = React.useMemo(() => {
        return getGridTimelineData(schedule);
    }, [schedule]);

    const timelineOffset = React.useMemo(() => {
        if (!timelineData?.intervals?.[0] || !schedule?.[0]?.timeStart) {
            return 0;
        }

        return unixDiff(timelineData.intervals[0], schedule[0].timeStart) * timelineStep;
    }, [timelineData, schedule, timelineStep]);

    /**
     * Рассчет высоты последнего отрезка времени, для исключения пустого пространства в конце расписания.
     * Высчитывается из разницы конца времени таймлайна и времени окончания последнего эпизода,
     * уменоженное на высоту в пикселях промежутка в 1 минуту.
     * +10 - доп. оступ в 10px для компенcации возможных смещений эпизодов, погрешностях, и чтобы просто не прилипало к концу.
     */
    const endOffset = React.useMemo(() => {
        if (!timelineData) {
            return 0;
        }

        const lastEpisode = cloneDeep(schedule)?.sort((a, b) => a.timeStop - b.timeStop).slice(-1)?.[0] ?? null;
        if (!lastEpisode) {
            return 0;
        }

        const {timeStop: timelineEndTime} = timelineData;
        const {timeStop: episodeEndTime} = lastEpisode;

        return (unixDiff(timelineEndTime, episodeEndTime) * timelineStep) + 10;

    }, [schedule, timelineData, timelineStep]);

    const firstScheduleTime = React.useMemo(() => {
        if (!schedule?.length || !timelineData?.intervals?.length) {
            return null;
        }

        const timeDifference = unixDiff(schedule[0].timeStart, timelineData.intervals[1]);

        return timeDifference >= 15 ? schedule[0].timeStart : null;
    }, [schedule, timelineData]);

    React.useEffect(() => {
        if (!!pdfSettings?.gridCardHeightSize) {
            setTimelineStep(parseFloat(pdfSettings.gridCardHeightSize));
        }
    }, []);

    if (!timelineData) {
        return null;
    }

    return (
        <div
            className={`pdf-schedule-grid-timeline ${event.stream.length <= 1 ? 'streams-hidden' : ''}`}
            style={{
                marginTop: event.stream.length <= 1 ? 0 : null,
            }}
        >
            {
                timelineData.intervals.map((interval, index) => (
                    <div
                        key={interval}
                        className={`pdf-schedule-grid-timeline__row`}
                        style={{
                            height: !!timelineOffset && index === 0 ? timelineStep * 60 - timelineOffset : timelineStep * 60,
                            maxHeight: index === timelineData.intervals.length - 1 ? endOffset : 'unset',
                        }}
                    >
                        <div className="pdf-schedule-grid-timeline__row-time">
                            {   index !== 0
                                ? unixFormat(interval, 'HH:mm')
                                : unixFormat(firstScheduleTime, 'HH:mm')
                            }
                        </div>
                    </div>
                ))
            }
            <ScheduleGridStreams
                event={event}
                streams={event.stream.filter(({id}) => schedule.some(({streamId}) => streamId === id))}
                page={page}
                episodes={schedule}
                episodesOffset={timelineOffset}
                timelineData={timelineData}
                minuteStep={timelineStep}
                colMinWidth={1}
                isStreamsHidden={event.stream.length <= 1}
                card={PdfCardGrid}
            />
        </div>
    )
};

export default SchedulePdfGrid;

